import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { InvitationDetailSheet } from '@/components/resident/InvitationDetailSheet';
import { Button } from '@/components/ui/button';
import { ResponsiveTable, type ResponsiveColumn } from '@/components/ui/responsive-table';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useResidentProperty } from '@/hooks/useResidentProperty';
import { useTranslation } from '@/hooks/useTranslation';
import { getInvitations, getPropertyConfig } from '@/services/api.client';
import type { Invitation } from '@/types';
import { formatDateTime } from '@/lib/utils';

type RsvpFilter = 'ALL' | 'confirmed' | 'pending' | 'declined';

function matchesRsvp(inv: Invitation, filter: RsvpFilter): boolean {
  if (filter === 'ALL') return true;
  if (filter === 'confirmed') return inv.rsvpStatus === 'confirmed';
  if (filter === 'declined') return inv.rsvpStatus === 'declined';
  // 'pending' covers null + 'not_responded'
  return inv.rsvpStatus === null || inv.rsvpStatus === 'not_responded';
}

const FILTER_EMPTY_KEY: Record<Exclude<RsvpFilter, 'ALL'>, string> = {
  confirmed: 'resident.visitors.emptyConfirmed',
  pending: 'resident.visitors.emptyAwaiting',
  declined: 'resident.visitors.emptyDeclined',
};

type TFn = (key: string, params?: Record<string, string | number>) => string;

function rsvpBadge(inv: Invitation, t: TFn): { label: string; className: string } {
  // Avoid the global `badge-status` class — it uppercases the label which makes
  // the longer "Awaiting reply" copy feel shouty. Use a softer outline pill.
  const base = 'rounded-full border px-2.5 py-0.5 text-xs font-medium normal-case';
  if (inv.rsvpStatus === 'confirmed')
    return {
      label: t('resident.visitors.badgeConfirmed'),
      className: `${base} border-[var(--status-green)]/30 text-[var(--status-green-text)]`,
    };
  if (inv.rsvpStatus === 'declined')
    return {
      label: t('resident.visitors.badgeDeclined'),
      className: `${base} border-destructive/30 text-destructive`,
    };
  return {
    label: t('resident.visitors.badgeAwaiting'),
    className: `${base} border-[var(--status-amber)]/30 text-[var(--status-amber-text)]`,
  };
}

function rsvpCell(inv: Invitation, t: TFn): ReactNode {
  const isPendingReg = inv.status === 'awaiting_visitor_registration';
  if (isPendingReg) {
    return (
      <span className="rounded-full border border-[var(--status-amber)]/30 px-2.5 py-0.5 text-xs font-medium text-[var(--status-amber-text)]">
        {t('resident.visitors.awaitingRegistration')}
      </span>
    );
  }
  const rsvp = rsvpBadge(inv, t);
  return <span className={rsvp.className}>{rsvp.label}</span>;
}

function buildInvitationColumns(t: TFn): ResponsiveColumn<Invitation>[] {
  return [
    {
      key: 'visitor',
      header: t('resident.visitors.colVisitor'),
      priority: 'primary',
      cell: (inv) => (
        <span className="text-sm font-medium text-[var(--text-primary)]">{inv.visitorName}</span>
      ),
    },
    {
      key: 'window',
      header: t('resident.visitors.colWindow'),
      priority: 'secondary',
      cell: (inv) => (
        <span className="text-sm text-[var(--text-secondary)]">
          {formatDateTime(inv.validFrom)} – {inv.validUntil ? formatDateTime(inv.validUntil) : '—'}
        </span>
      ),
    },
    {
      key: 'rsvp',
      header: t('resident.visitors.colRsvp'),
      priority: 'primary',
      cell: (inv) => rsvpCell(inv, t),
    },
  ];
}

export function MyVisitorsPage() {
  const prop = useResidentProperty();
  const { isLoading: isAuthLoading } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<RsvpFilter>('ALL');
  const [selected, setSelected] = useState<Invitation | null>(null);

  const filterTabs: { label: string; value: RsvpFilter }[] = [
    { label: t('resident.visitors.filterAll'), value: 'ALL' },
    { label: t('resident.visitors.filterConfirmed'), value: 'confirmed' },
    { label: t('resident.visitors.filterAwaiting'), value: 'pending' },
    { label: t('resident.visitors.filterDeclined'), value: 'declined' },
  ];

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'invitations',
      prop?.propertyId,
      { statusIn: 'active,awaiting_visitor_registration' },
    ],
    queryFn: () =>
      prop
        ? getInvitations(prop.propertyId, { statusIn: 'active,awaiting_visitor_registration' })
        : null,
    enabled: !!prop,
  });

  const { data: propertyConfig } = useQuery({
    queryKey: ['property-config', prop?.propertyId],
    queryFn: () => (prop ? getPropertyConfig(prop.propertyId) : null),
    enabled: !!prop,
    staleTime: 10 * 60 * 1000,
  });

  const allInvitations = data?.data ?? [];
  const filtered = allInvitations.filter((inv) => matchesRsvp(inv, activeTab));
  // Distinguish "you have no invitations at all" from "none match this filter".
  const isFilteredEmpty = activeTab !== 'ALL' && allInvitations.length > 0;

  return (
    <div className="space-y-4 pb-24">
      <h1 className="text-2xl">{t('resident.visitors.title')}</h1>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {filterTabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setActiveTab(tab.value)}
            className={[
              'rounded-full px-4 py-1.5 text-xs font-medium transition-colors',
              activeTab === tab.value
                ? 'text-[var(--text-inverse)]'
                : 'border border-[var(--bg-surface-raised)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
            ].join(' ')}
            style={activeTab === tab.value ? { background: 'var(--cta-primary-bg)' } : {}}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-[var(--bg-surface-raised)] overflow-x-auto">
        {isError ? (
          <div
            role="alert"
            className="rounded-xl border border-destructive/40 bg-destructive/5 p-4"
          >
            <p className="text-sm font-medium">{t('resident.visitors.loadError')}</p>
            <p className="text-sm text-muted-foreground">{t('resident.visitors.tryAgainLater')}</p>
          </div>
        ) : isLoading || isAuthLoading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <Users className="size-10 mx-auto mb-3 text-muted-foreground/30" />
            {isFilteredEmpty ? (
              <>
                <p className="text-sm font-medium text-muted-foreground">
                  {t(FILTER_EMPTY_KEY[activeTab as Exclude<RsvpFilter, 'ALL'>])}
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  {t('resident.visitors.tryDifferentFilter')}
                </p>
                <Button
                  variant="link"
                  size="sm"
                  className="mt-2"
                  onClick={() => setActiveTab('ALL')}
                >
                  {t('resident.visitors.showAll')}
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('resident.visitors.emptyTitle')}
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  {t('resident.visitors.emptyBody')}
                </p>
                <Button variant="link" size="sm" asChild className="mt-2">
                  <Link to="/resident/invite">{t('resident.visitors.inviteSomeone')}</Link>
                </Button>
              </>
            )}
          </div>
        ) : (
          <ResponsiveTable
            columns={buildInvitationColumns(t)}
            rows={filtered}
            getRowKey={(inv) => inv.id}
            onRowClick={(inv) => setSelected(inv)}
            rowLabel={(inv) => t('resident.visitors.rowLabel', { name: inv.visitorName })}
            caption={t('resident.visitors.caption')}
          />
        )}
      </div>

      {/* FAB — lifted above the mobile bottom nav (+ safe area) on phones */}
      <div className="fixed bottom-8 right-8 max-md:bottom-[calc(env(safe-area-inset-bottom)+5rem)]">
        <Link
          to="/resident/invite"
          className="flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-[var(--text-inverse)] shadow-lg transition-opacity hover:opacity-90"
          style={{ background: 'var(--cta-primary-bg)' }}
        >
          {t('resident.visitors.fab')}
        </Link>
      </div>

      {/* Detail sheet */}
      {prop && (
        <InvitationDetailSheet
          open={!!selected}
          onOpenChange={(o) => {
            if (!o) setSelected(null);
          }}
          propertyId={prop.propertyId}
          invitation={selected}
          estateName={propertyConfig?.name}
        />
      )}
    </div>
  );
}
